import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ADDRESS_TYPES, NIGERIAN_STATE_OPTIONS } from '@solar/contracts';

import { AddressAutocompleteField } from '../../../components/addresses/AddressAutocompleteField';
import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { customersApi } from '../../../features/customers/customers.api';
import type { AddressAutocompleteSuggestion } from '../../../features/locations/locations.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import styles from '../AppPages.module.css';

export function AddressesPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const addresses = useQuery({
    queryKey: ['customer', 'addresses'],
    queryFn: () => customersApi.listAddresses(),
  });

  const [addressType, setAddressType] = useState<string>(ADDRESS_TYPES[0]);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [localGovernmentArea, setLocalGovernmentArea] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [selectionNotice, setSelectionNotice] = useState(false);

  const add = useMutation({
    mutationFn: () =>
      customersApi.addAddress({
        addressType,
        addressLine1,
        addressLine2: addressLine2 === '' ? undefined : addressLine2,
        localGovernmentArea: localGovernmentArea === '' ? undefined : localGovernmentArea,
        city,
        stateCode,
        countryCode: 'NG',
        postalCode: postalCode === '' ? undefined : postalCode,
        isDefault: addresses.data?.length === 0 ? 'true' : 'false',
      }),
    onSuccess: async () => {
      setAddressLine1('');
      setAddressLine2('');
      setLocalGovernmentArea('');
      setCity('');
      setStateCode('');
      setPostalCode('');
      setSelectionNotice(false);
      await queryClient.invalidateQueries({ queryKey: ['customer', 'addresses'] });
    },
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Addresses</h1>
      </header>

      <section className={styles.card} aria-labelledby="add-address-heading">
        <h2 className={styles.cardTitle} id="add-address-heading">
          Add address
        </h2>

        {add.isError && <Alert tone="error">{toDisplayMessage(add.error)}</Alert>}
        {selectionNotice && (
          <Alert tone="info">Address details applied. Review them before you save.</Alert>
        )}

        <form
          className={styles.customerForm}
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <SelectField
            label="Type"
            value={addressType}
            onChange={(event) => {
              setAddressType(event.target.value);
            }}
          >
            {ADDRESS_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </SelectField>
          <AddressAutocompleteField
            value={addressLine1}
            required
            onChange={(value) => {
              setAddressLine1(value);
              setSelectionNotice(false);
            }}
            onSelect={(suggestion: AddressAutocompleteSuggestion) => {
              setAddressLine1(suggestion.line1 ?? suggestion.displayAddress);
              setAddressLine2(suggestion.line2 ?? '');
              setLocalGovernmentArea(suggestion.area ?? '');
              setCity(suggestion.city ?? '');
              setStateCode(suggestion.stateCode ?? '');
              setPostalCode(suggestion.postalCode ?? '');
              setSelectionNotice(true);
            }}
          />
          <FormField
            label="Address line 2"
            name="addressLine2"
            value={addressLine2}
            autoComplete="address-line2"
            onChange={(event) => {
              setAddressLine2(event.target.value);
            }}
          />
          <FormField
            label="City"
            name="city"
            value={city}
            required
            onChange={(event) => {
              setCity(event.target.value);
            }}
          />
          <FormField
            label="Local government area"
            name="localGovernmentArea"
            value={localGovernmentArea}
            onChange={(event) => {
              setLocalGovernmentArea(event.target.value);
            }}
          />
          <FormField
            label="Postal code"
            name="postalCode"
            value={postalCode}
            autoComplete="postal-code"
            onChange={(event) => {
              setPostalCode(event.target.value);
            }}
          />
          <SelectField
            label="State"
            name="stateCode"
            value={stateCode}
            required
            onChange={(event) => setStateCode(event.target.value)}
          >
            <option value="" disabled>
              Choose state
            </option>
            {NIGERIAN_STATE_OPTIONS.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </SelectField>
          <Button type="submit" disabled={add.isPending}>
            Add address
          </Button>
        </form>
      </section>

      <section className={styles.card} aria-labelledby="addresses-heading">
        <h2 className={styles.cardTitle} id="addresses-heading">
          Saved addresses
        </h2>

        {addresses.isPending && <PageSpinner label="Loading addresses" />}
        {addresses.isError && <Alert tone="error">{toDisplayMessage(addresses.error)}</Alert>}

        {addresses.isSuccess && addresses.data.length === 0 && (
          <p className={styles.empty}>No addresses saved yet.</p>
        )}

        {addresses.isSuccess && addresses.data.length > 0 && (
          <ul>
            {addresses.data.map((address) => (
              <li key={address.id}>
                <strong>{address.addressType.replace(/_/g, ' ')}</strong>
                {address.isDefault === 'true' ? ' (default)' : ''}: {address.addressLine1},{' '}
                {address.city}, {address.stateCode}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
